<script setup>
import { watch, computed, ref, onUnmounted } from 'vue';
import { emitter } from 'shared/helpers/mitt';
import { useI18n } from 'vue-i18n';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TicketsAPI from 'dashboard/api/tickets';

const props = defineProps({
  contactId: { type: [String, Number], required: true },
});

const emit = defineEmits(['selectTicket']);

useI18n();

const contactId = computed(() => props.contactId);
const isLoading = ref(false);
const tickets = ref([]);

const fetchTickets = async () => {
  if (!contactId.value) {
    tickets.value = [];
    return;
  }

  isLoading.value = true;
  try {
    const response = await TicketsAPI.get({ contact_id: contactId.value });
    tickets.value = response.data.payload || [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching contact tickets:', error);
    tickets.value = [];
  } finally {
    isLoading.value = false;
  }
};

const getStatusClass = status => {
  const classes = {
    open: 'bg-yellow-100 text-yellow-800',
    in_progress: 'bg-blue-100 text-blue-800',
    resolved: 'bg-green-100 text-green-800',
    closed: 'bg-gray-100 text-gray-800',
  };
  return classes[status] || 'bg-gray-100 text-gray-800';
};

const formatStatus = status => {
  return (
    status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) ||
    'Unknown'
  );
};

const selectTicket = ticket => {
  emit('selectTicket', ticket);
};

watch(
  contactId,
  newContactId => {
    if (newContactId) {
      fetchTickets();
    }
  },
  { immediate: true }
);

const onRefreshRequest = () => {
  if (contactId.value) {
    fetchTickets();
  }
};
emitter.on('refresh_tickets', onRefreshRequest);

onUnmounted(() => {
  emitter.off('refresh_tickets', onRefreshRequest);
});
</script>

<template>
  <div>
    <div
      v-if="isLoading"
      class="flex items-center justify-center py-8 text-n-slate-11"
    >
      <Spinner />
    </div>
    <div
      v-else-if="tickets.length"
      class="flex flex-col max-h-[300px] overflow-y-auto"
    >
      <div
        v-for="ticket in tickets"
        :key="ticket.id"
        class="flex items-center justify-between px-4 py-3 border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1 cursor-pointer"
        @click="selectTicket(ticket)"
      >
        <div class="flex flex-col gap-1 min-w-0">
          <div class="flex flex-col">
            <span class="text-xs font-mono text-n-slate-11">
              {{ ticket.folio || `#${ticket.display_id}` }}
            </span>
            <span class="text-sm font-medium text-n-slate-12 truncate">
              {{ ticket.title }}
            </span>
          </div>
          <span
            class="inline-flex w-fit px-2 py-0.5 text-xs font-medium rounded-full"
            :class="getStatusClass(ticket.status)"
          >
            {{ formatStatus(ticket.status) }}
          </span>
        </div>
        <i class="i-lucide-chevron-right text-n-slate-11 flex-shrink-0" />
      </div>
    </div>
    <p v-else class="px-6 py-6 text-sm leading-6 text-center text-n-slate-11">
      {{ t('TICKETS.SIDEBAR.NO_TICKETS') }}
    </p>
  </div>
</template>
